import React, { useEffect, useId, useRef, useState } from 'react';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import useFocusTrap from '../hooks/useFocusTrap';

/**
 * 閉じている間、最後に押されたボタンを覚える。
 * macOS の Safari ではボタンを押してもフォーカスが移らないことがあり、開いた時点のフォーカスでは戻す先が分からないため
 * （SCR-P-001 §7.2「押されたボタンそのもの」と同じ理由）。
 * 開く契機がボタンの押下とは限らない（アップロードの完了・エラー）ため、呼び出す側からは受け取らない。
 */
function useLastClickedButton(isOpen) {
  const lastClickedRef = useRef(null);

  useEffect(() => {
    if (isOpen) return undefined;
    const remember = (e) => {
      lastClickedRef.current = e.target.closest('button');
    };
    // 押されたボタンの onClick でモーダルが開くため、それより前（キャプチャ）に覚える
    document.addEventListener('click', remember, true);
    return () => document.removeEventListener('click', remember, true);
  }, [isOpen]);

  return lastClickedRef;
}

/** 開いている間だけ描画する本体。フォーカスの処理を、開いたとき・閉じたときに走らせるため分けている */
function ConfirmDialog({
  title,
  children,
  onConfirm,
  onCancel,
  confirmText,
  cancelText,
  confirmClassName,
  disabled,
  lastClickedRef,
}) {
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);
  const titleId = useId();
  const [opener] = useState(() => {
    const active = document.activeElement;
    return active && active !== document.body ? active : lastClickedRef.current;
  });

  useBodyScrollLock(true);
  // 下のフォーカスの処理より前に呼ぶ（戻す先が見つからないときに、開いた時点のフォーカスを覚えるため）
  useFocusTrap(dialogRef, opener);

  // 取り消しのボタンへ置く（Enter だけで実行されないように）。押せない間はダイアログ自身に置く。
  // 押したボタンが処理中に無効になると、フォーカスが body へ落ちるかはブラウザによるため、待たずに移す。
  // 押せるように戻ると、ダイアログ自身にあるフォーカスをここで取り消しのボタンへ置き直す
  useEffect(() => {
    const target = disabled ? dialogRef.current : cancelRef.current || confirmRef.current;
    target.focus();
  }, [disabled]);

  // Esc で閉じる（VI 定義 §10.4）。取り消しのボタンが無いモーダルは「閉じる」だけを持つため、それを押したのと同じにする
  useEffect(() => {
    const close = onCancel || onConfirm;
    const handleKey = (e) => {
      if (e.key === 'Escape' && !disabled) close();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onCancel, onConfirm, disabled]);

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ outline: 'none' }}
        ref={dialogRef}
      >
        <h3 className="modal-title" id={titleId}>{title}</h3>
        <div style={{ margin: '0 0 20px 0' }}>{children}</div>
        <div className="modal-actions">
          {onCancel && (
            <button
              type="button"
              className="modal-button-cancel"
              onClick={onCancel}
              disabled={disabled}
              ref={cancelRef}
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            className={confirmClassName}
            onClick={onConfirm}
            disabled={disabled}
            ref={confirmRef}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 汎用確認モーダルコンポーネント
 *
 * 開いたら取り消しのボタン（無ければ確定のボタン）へフォーカスを移し、開いている間は Tab をモーダルの中に閉じ込め、
 * 閉じたら元の要素へ戻す。Esc で閉じる（VI 定義 §10.4、SCR-M-001 §7.2）。
 * onCancel を省けるのは、onConfirm が閉じるだけのときに限る（Esc が onConfirm を呼ぶため）。
 *
 * 使用例:
 *   <ConfirmModal
 *     isOpen={showModal}
 *     title="確認"
 *     onConfirm={handleConfirm}
 *     onCancel={handleCancel}
 *   >
 *     <p>本当に実行しますか？</p>
 *   </ConfirmModal>
 */
function ConfirmModal({
  isOpen,
  title,
  children,
  onConfirm,
  onCancel,
  confirmText = 'はい、実行する',
  cancelText = 'いいえ',
  confirmClassName = 'modal-button-confirm',
  disabled = false,
}) {
  const lastClickedRef = useLastClickedButton(isOpen);

  if (!isOpen) return null;

  return (
    <ConfirmDialog
      title={title}
      onConfirm={onConfirm}
      onCancel={onCancel}
      confirmText={confirmText}
      cancelText={cancelText}
      confirmClassName={confirmClassName}
      disabled={disabled}
      lastClickedRef={lastClickedRef}
    >
      {children}
    </ConfirmDialog>
  );
}

export default ConfirmModal;
